import { makePage } from '@/components/page'
import { useVenueContext } from '@/services/venue'
import Link from 'next/link'
import { LegacyInput, InputRef, touchInputs, SelectRef, Select } from '@/components/input'
import { sessionStorage, stubStorage } from '@/utils/env'
import React, { useRef, useState, useEffect } from 'react'
import { VenueBottomButton } from '@/components/bottom-button'
import { useAppContext } from '@/pages/_app'
import { VenueBookTableBody } from 'shared/types/api'
import { range } from 'shared/utils/array'
import addDays from 'date-fns/addDays'
import format from 'date-fns/format'
import setMinutes from 'date-fns/setMinutes'
import setHours from 'date-fns/setHours'
import { CalendarIcon } from '@/components/calendar-icon'
import { ClockIcon } from '@/components/clock-icon'
import { PersonIcon } from '@/components/person-icon'
import { Spinner } from '@/components/spinner'
import { InputPhone } from '@/components/input-phone'
import { capitalize } from 'shared/utils/string'
import { generateTimeOptions, localizeTimeString, parseTime } from '@/utils/date'
import { returnOrThrow } from 'shared/utils/types'
import { PageMeta } from '@/components/page-meta'
import { HeadPanel } from '@/components/head-panel'
import { TrixContent } from '@/components/trix-content'
import { checkPersonalDataConsent, PersonalDataConsent } from '@/components/personal-data-consent'

export default makePage(
  {
    clientRenderOnly: {},
  },
  function BookTablePage() {
    const { apiClient, tracker, t } = useAppContext()
    const venueContext = useVenueContext()!
    const { venueName, venue, menuRoute, locale } = venueContext

    const refs = {
      numberOfGuests: useRef<SelectRef>(null),
      date: useRef<SelectRef>(null),
      time: useRef<SelectRef>(null),
      phone: useRef<InputRef>(null),
      comment: useRef<InputRef>(null),
      name: useRef<InputRef>(null),
      email: useRef<InputRef>(null),
    }

    type Status =
      | {
          type: 'initial'
        }
      | {
          type: 'loading'
          sentBookingDate: Date
        }
      | {
          type: 'reservationMade'
          formResult: VenueBookTableBody
        }
      | {
          type: 'error'
        }

    const [status, setStatus] = useState<Status>({
      type: 'initial',
    })
    const [todayIsChosen, setTodayIsChosen] = useState(true)

    const bookTableConfig = venue.features.vas?.bookTable

    const now = new Date()

    const opensAt = returnOrThrow(parseTime(bookTableConfig?.opensAt || '08:00'))
    const closesAt = returnOrThrow(parseTime(bookTableConfig?.closesAt || '23:59'))
    const stepMinutes = 15
    const timeOptions = generateTimeOptions(opensAt, closesAt, stepMinutes)

    const timeOptionsToday = timeOptions.filter((v) => {
      return v > now
    })

    useEffect(() => {
      if (timeOptionsToday.length === 0) {
        setTodayIsChosen(false)
      }
    }, [])

    if (!bookTableConfig) {
      return null
    }

    const formId = 'bookTable'

    return (
      <div>
        <PageMeta
          subTitle={t('bookTable:bookTable')}
          description={t('bookTable:description', { venueName: venue.title })}
        />
        <HeadPanel url={menuRoute.path} text={t('bookTable:bookTable')} />
        {(() => {
          if (status.type === 'reservationMade') {
            return (
              <>
                <div className="mt-4 mb-3 bg-backAccent dark:bg-backAccentDark px-5 py-5 text-lg leading-tight">
                  {t('bookTable:thankYou')}
                </div>
                {bookTableConfig.noticeHtml && (
                  <TrixContent
                    className="px-5 mt-2 text-lg"
                    unescapedHtml={bookTableConfig.noticeHtml}
                  />
                )}
                <div className="flex justify-between px-5 mt-6 text-lg">
                  <div className="flex items-center">
                    <CalendarIcon />
                    <span className="ml-1">
                      {format(new Date(status.formResult.date), 'eee, d MMM', { locale })}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-5 h-5">
                      <ClockIcon />
                    </span>
                    <span className="ml-1">
                      {format(new Date(status.formResult.date), 'HH:mm', { locale })}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <PersonIcon />
                    <span className="ml-1">
                      {(() => {
                        const { numberOfGuests } = status.formResult
                        return numberOfGuests === 999 ? '10+' : numberOfGuests
                      })()}{' '}
                      {t('bookTable:people')}
                    </span>
                  </div>
                </div>
                <div className="px-5 mt-6 text-lg">
                  {status.formResult.name && (
                    <div className="mt-1">
                      {t('common:name')}: {status.formResult.name}
                    </div>
                  )}
                </div>
                <div className="px-5 mt-2 text-lg">
                  {status.formResult.phone && (
                    <div className="mt-1">
                      {t('common:phoneNumber')}: {status.formResult.phone}
                    </div>
                  )}
                </div>
                <div className="px-5 mt-2 text-lg">
                  {status.formResult.email && (
                    <div className="mt-1">
                      {t('common:email')}: {status.formResult.email}
                    </div>
                  )}
                </div>
                <div className="px-5 mt-2 text-lg">
                  {status.formResult.comment && (
                    <div className="mt-1 whitespace-pre-line">
                      {t('common:note')}: {status.formResult.comment}
                    </div>
                  )}
                </div>
              </>
            )
          }

          return (
            <>
              {bookTableConfig.addressHtml && (
                <TrixContent
                  className="mt-4 mb-3 bg-backAccent dark:bg-backAccentDark px-5 py-5 text-lg leading-tight"
                  unescapedHtml={bookTableConfig.addressHtml}
                />
              )}
              <form
                id={formId}
                onSubmit={async (event) => {
                  event.preventDefault()
                  const isValid = touchInputs(Object.values(refs)).isValid
                  if (!isValid || status.type !== 'initial') {
                    return
                  }

                  if (!checkPersonalDataConsent()) {
                    return
                  }

                  const now = new Date()
                  const bookingDate = addDays(now, Number.parseInt(refs.date.current!.value, 10))
                  // we are sure hours and minutes are defined because we generated the time string ourselves
                  const [hours, minutes] = refs.time.current!.value.split(':').map(Number) as [
                    number,
                    number,
                  ]
                  const bookingDateAndTime = setMinutes(setHours(bookingDate, hours), minutes)
                  setStatus({
                    type: 'loading',
                    sentBookingDate: bookingDateAndTime,
                  })

                  const body: VenueBookTableBody = {
                    date: format(bookingDateAndTime, `yyyy-MM-dd'T'HH:mm:ssxxx`),
                    numberOfGuests: Number.parseInt(refs.numberOfGuests.current!.value, 10),
                    name: refs.name.current!.value,
                    phone: refs.phone.current!.implicitValue,
                    email: refs.email.current!.value,
                    comment: refs.comment.current!.value,
                  }

                  tracker.track('user/bookTable', { bookTable: body })
                  const apiResponse = await apiClient.fetch(`/venue/${venueName}/book-table`, {
                    method: 'POST',
                    body: JSON.stringify(body),
                  })
                  window.scroll(0, 0)

                  if (apiResponse instanceof Error) {
                    setStatus({
                      type: 'error',
                    })
                    tracker.trackError(apiResponse, {
                      bookTableError: true,
                    })
                    return
                  }

                  if (apiResponse.status !== 200) {
                    setStatus({
                      type: 'error',
                    })
                    tracker.trackError(new Error('bookTable: error response'), {
                      bookTableError: true,
                      apiResponse,
                    })
                    return
                  }

                  setStatus({
                    type: 'reservationMade',
                    // pass value, not implicit value to show the user the exact text
                    // they have seen in the phone input
                    formResult: { ...body, phone: refs.phone.current!.value },
                  })
                }}
              >
                <div className="px-5 mt-2">
                  <div className="mt-6">
                    <Select
                      storage={sessionStorage}
                      ref={refs.numberOfGuests}
                      id="bookTable/numberOfGuests"
                      label={t('bookTable:partySize')}
                      options={[
                        ...range(1, 10)
                          .map(String)
                          .map((v) => {
                            return { id: v, text: v }
                          }),
                        { id: '999', text: capitalize(t('bookTable:moreThanTen')) },
                      ]}
                      initialOptionId="2"
                    />
                  </div>
                  <div className="mt-6 flex">
                    <div className="flex-grow" style={{ flexBasis: '50%' }}>
                      <Select
                        storage={stubStorage}
                        ref={refs.date}
                        id="bookTable/date"
                        label={t('common:date')}
                        options={[
                          ...(timeOptionsToday.length > 0
                            ? [{ id: '0', text: t('common:today') }]
                            : []),
                          { id: '1', text: t('common:tomorrow') },
                          ...range(2, 7).map((daysFromNow) => {
                            const now = new Date()
                            const date = addDays(now, daysFromNow)
                            return {
                              id: daysFromNow.toString(),
                              text: capitalize(
                                format(date, 'eeee, d MMMM', {
                                  locale,
                                }),
                              ),
                            }
                          }),
                        ]}
                        onChange={(value) => {
                          setTodayIsChosen(Number.parseInt(value, 10) === 0)
                        }}
                      />
                    </div>
                    <div className="w-4 flex-shrink-0"></div>
                    <div className="flex-grow" style={{ flexBasis: '50%' }}>
                      <Select
                        storage={stubStorage}
                        ref={refs.time}
                        id="bookTable/time"
                        label={t('common:time')}
                        options={(todayIsChosen ? timeOptionsToday : timeOptions)
                          .map((v) => {
                            return format(v, 'HH:mm', { locale })
                          })
                          .map((v) => {
                            return {
                              id: v,
                              text: localizeTimeString(v),
                            }
                          })}
                      />
                    </div>
                  </div>
                  <div className="mt-6">
                    <LegacyInput
                      storage={sessionStorage}
                      ref={refs.name}
                      id="bookTable/name"
                      label={t('common:name')}
                      type="text"
                      value=""
                    />
                  </div>
                  <div className="mt-6">
                    <InputPhone label={t('common:phone')} ref={refs.phone} required />
                  </div>
                  <div className="mt-6 custom-book-table-email-field">
                    <LegacyInput
                      storage={sessionStorage}
                      ref={refs.email}
                      id="bookTable/email"
                      label={t('common:email')}
                      type="email"
                      value=""
                    />
                  </div>
                  <div className="mt-6 mb-6">
                    <LegacyInput
                      storage={sessionStorage}
                      height={2}
                      ref={refs.comment}
                      id="bookTable/comment"
                      label={t('bookTable:note')}
                      type="text"
                      value=""
                    />
                  </div>

                  <div className="mt-2">
                    <PersonalDataConsent {...{ venue }} />
                  </div>
                </div>
              </form>
            </>
          )
        })()}
        <VenueBottomButton>
          {status.type === 'reservationMade' ? (
            <Link
              href={menuRoute.path}
              className="flex justify-center items-center h-12 bg-back dark:bg-backAccentDark dark:text-primaryAccentDark font-medium rounded text-center"
            >
              <span className="text-lg">{t('back')}</span>
            </Link>
          ) : (
            <button
              className="flex items-center w-full h-12 text-lg justify-center rounded-lg bg-back dark:bg-backAccentDark dark:text-primaryAccentDark font-medium"
              type="submit"
              form={formId}
            >
              {(() => {
                if (status.type === 'error') {
                  return (
                    <span className="font-medium text-negative dark:text-negativeDark cursor-not-allowed">
                      {t('common:error')}
                    </span>
                  )
                }
                if (status.type === 'loading') {
                  return (
                    <div className="w-6 inline-block mx-auto">
                      <Spinner />
                    </div>
                  )
                }

                return <span>{t('bookTable:book')}</span>
              })()}
            </button>
          )}
        </VenueBottomButton>
      </div>
    )
  },
)

export {
  getVenueStaticPaths as getStaticPaths,
  getVenueStaticProps as getStaticProps,
} from '@/services/venue'
